import {brandMonitoringTiers} from '@/content/brand-monitoring/constants'
import {getSolutionItemContent} from '@/content/user/caseSolutionsContent'
import {
  BrandSubscriptionPaymentFrequency,
  BrandSubscriptionTier,
} from '@/utils/brand-subscription/constants'
import {CaseSolutionItemType} from '@/utils/case-solution/constants'
import {getSolutionServiceFees} from '@/utils/case-solution/helpers'
import {Locale} from '@/utils/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {Currency} from '@/utils/currency'
import {PricingTabType} from '@/utils/pricing'
import {getTierBasePrice} from '@/utils/pricing/brand-subscription-payment/calculations'
import {routeTo} from '@/utils/router'
import {
  legalServices,
  serviceNameToTitle,
  servicesForPriceList,
} from '@/utils/service-request/constants'
import {LegalServiceGroups, Service} from '@/utils/service-request/types'
import {getServicePrices, services} from '@/utils/supported-services/services'
import {supportedServices} from '@/utils/supported-services/supported-services'

export type PricingTabContent = {
  title: string
  // Each tab prices something different, so each says so in its own h1 rather than sharing one.
  heroTitle: string
  metaTitle: string
  metaDescription: string
}

export const pricingTabs: Record<PricingTabType, PricingTabContent> = {
  [PricingTabType.TrademarkRegistration]: {
    title: 'pricing-reg-title',
    heroTitle: 'pricing.trademark-registration-hero-title',
    metaTitle: 'pricing.trademark-registration-meta-title',
    metaDescription: 'pricing.trademark-registration-meta-description',
  },
  [PricingTabType.TrademarkRenewal]: {
    title: serviceNameToTitle[Service.tmRenewal],
    heroTitle: 'pricing.trademark-renewal-hero-title',
    metaTitle: 'pricing.trademark-renewal-meta-title',
    metaDescription: 'pricing.trademark-renewal-meta-description',
  },
  [PricingTabType.TrademarkOwnershipTransfer]: {
    title: serviceNameToTitle[Service.tmAssignmentAgreement],
    heroTitle: 'pricing.trademark-ownership-transfer-hero-title',
    metaTitle: 'pricing.trademark-ownership-transfer-meta-title',
    metaDescription: 'pricing.trademark-ownership-transfer-meta-description',
  },
  [PricingTabType.LegalServices]: {
    title: 'pricing-other-services-title',
    heroTitle: 'pricing.legal-services-hero-title',
    metaTitle: 'pricing.legal-services-meta-title',
    metaDescription: 'pricing.legal-services-meta-description',
  },
  [PricingTabType.BrandProtection]: {
    title: 'pricing-brand-protection-title',
    heroTitle: 'pricing.brand-protection-hero-title',
    metaTitle: 'pricing.brand-protection-meta-title',
    metaDescription: 'pricing.brand-protection-meta-description',
  },
}

export const getPricingContent = () => {
  return {
    header: {
      buttonLinkText: 'button-start-with-registration',
      buttonLinkHref: routeTo.verification.form(),
    },
    hero: {
      subtitle: 'pricing-hero-subtitle',
      imageSrc: '/images/hero/ice-cream-with-price-tag.svg',
      imageAlt: 'Ice cream with a price tag',
    },
    benefits: [
      {
        imageSrc: '/images/icons/cost.svg',
        imageAlt: 'Price tag',
        title: 'pricing-benefit-0-title',
        description: 'pricing-benefit-0-desc',
      },
      {
        imageSrc: '/images/icons/scales-of-justice.svg',
        imageAlt: 'Scales of justice',
        title: 'pricing-benefit-1-title',
        description: 'pricing-benefit-1-desc',
      },
      {
        imageSrc: '/images/icons/documents-with-check-icon.svg',
        imageAlt: 'Document with a checkmark',
        title: 'pricing-benefit-2-title',
        description: 'pricing-benefit-2-desc',
      },
      {
        imageSrc: '/images/icons/document-with-stamp.svg',
        imageAlt: 'Document with a stamp',
        title: 'pricing-benefit-3-title',
        description: 'pricing-benefit-3-desc',
      },
    ],
    registration: {
      subtitle: 'pricing-reg-subtitle',
      benefitsTitle: 'pricing-fee-title',
      benefits: [
        'pricing-reg-fee-contains-0',
        'pricing-reg-fee-contains-3',
        'pricing-reg-fee-contains-1',
        'pricing-reg-fee-contains-2',
        'pricing-reg-fee-contains-4',
        'pricing-reg-fee-contains-5',
      ],
      calculator: {
        startButtonText: 'button-start-with-lawyer-check',
        continueButtonText: 'button-continue-with-free-lawyer-check',
      },
    },
    calculator: {
      title: 'pricing-calculator-title',
      description: 'pricing-calculator-subtitle',
      recommendedCountriesTitle: 'pricing-recommended-countries-title',
      otherCountriesTitle: 'pricing-other-countries-title',
      includedClassesTitle: 'pricing-reg-table-header-4',
      estimatedPriceTitle: 'pricing-estimated-price-title',
      usWarningTooltipText: 'ver-us-warning-tooltip-text',
      selectedClassesTitle: 'pricing-calculator-classes-title',
      numOfTrademarksTitle: 'pricing-calculator-num-of-trademarks-title',
      learnMoreAboutClassesLinkText: 'pricing-calculator-classes-desc',
      learnMoreAboutClassesLinkHref: {
        [Locale.en]: routeTo.tmAcademy.article('understanding-goods-and-services-selection'),
        [Locale.sk]: routeTo.tmAcademy.article('ako-funguje-vyber-tovarov-a-sluzieb'),
        [Locale.cs]: routeTo.tmAcademy.article('jak-funguje-vyber-zbozi-a-sluzeb'),
        [Locale.es]: routeTo.tmAcademy.article(
          'comprendiendo-la-seleccion-de-productos-y-servicios',
        ),
      } as Partial<Record<Locale, string>>,
      totalPriceTitle: 'pricing-reg-table-header-3',
      classesTitle: 'pricing-reg-table-header-7',
      countriesTitle: 'countries-text',
      multiCountryDiscountTitle: 'multi-country-discount-title',
      serviceFeeDiscountTitle: 'pricing-reg-table-header-9',
      discountCouponTitle: 'forms.discount-coupon',
      priceExclVatTitle: 'forms.price-excl-vat',
      priceInclVatTitle: 'forms.price-incl-vat',
      vatTitle: 'forms.price-vat',
      vatNotIncludedNote: 'pricing-calculator-vat-not-included',
      searchPlaceholder: 'search-country-placeholder',
    },
    discount: {
      title: 'package-discount-title',
      description: 'package-discount-desc',
      imageSrc: '/images/discount-gift.svg',
      imageAlt: 'Discount gift',
    },
    legalServices: {
      serviceKey: supportedServices.Legal,
      subtitle: 'pricing-other-services-table-note',
      services: services.Legal,
    },
    brandProtection: {
      subtitle: 'pricing-brand-protection-table-note',
      monitoring: {
        title: 'pricing-brand-protection-monitoring-title',
        services: [BrandSubscriptionTier.v2Essential, BrandSubscriptionTier.v2Advanced],
      },
      enforcement: {
        title: 'pricing-brand-protection-enforcement-title',
        services: [
          {type: CaseSolutionItemType.takedown},
          {
            type: CaseSolutionItemType.ceaseDesistLetter,
            platform: undefined,
            jurisdiction: undefined,
            option: 'simple',
          },
          {type: CaseSolutionItemType.warningLetter},
          {type: CaseSolutionItemType.negotiations},
          {
            type: CaseSolutionItemType.domainDispute,
            platform: undefined,
            jurisdiction: undefined,
            option: 'resolution',
          },
          {
            type: CaseSolutionItemType.opposition,
            platform: undefined,
            jurisdiction: Jurisdiction.UK,
            option: 'without-reasoning',
          },
          {
            type: CaseSolutionItemType.opposition,
            platform: undefined,
            jurisdiction: Jurisdiction.EU,
            option: 'with-reasoning',
          },
          {
            type: CaseSolutionItemType.cancellation,
            platform: undefined,
            jurisdiction: Jurisdiction.EU,
            option: 'with-reasoning',
          },
          {type: CaseSolutionItemType.legalConsultation},
          {type: CaseSolutionItemType.usLetterOfProtest},
        ],
      },
    },
    trademarkRenewal: {
      subtitle: 'pricing-tm-renewal-subtitle',
      benefitsList: [
        {
          title: 'pricing-tm-renewal-fee-contains-0-title',
          description: 'pricing-tm-renewal-fee-contains-0-desc',
        },
        {
          title: 'pricing-tm-renewal-fee-contains-1-title',
          description: 'pricing-tm-renewal-fee-contains-1-desc',
        },
        {
          title: 'pricing-tm-renewal-fee-contains-2-title',
          description: 'pricing-tm-renewal-fee-contains-2-desc',
        },
        {
          title: 'pricing-tm-renewal-fee-contains-3-title',
          description: 'pricing-tm-renewal-fee-contains-3-desc',
        },
        {
          title: 'pricing-tm-renewal-fee-contains-4-title',
          description: 'pricing-tm-renewal-fee-contains-4-desc',
        },
      ],
      calculator: {
        startButtonText: 'button-start-with-tm-renewal',
        continueButtonText: 'button-continue-with-tm-renewal',
      },
    },
    trademarkOwnershipTransfer: {
      subtitle: 'pricing.tm-ownership-subtitle',
      trademarksIncludedTitle: 'trademarks-included-title',
      additionalTrademarkFeeTitle: 'each-additional-trademark-title',
      benefitsList: [
        {
          title: 'pricing.tm-ownership-fee-contains-0-title',
          description: 'pricing.tm-ownership-fee-contains-0-desc',
        },
        {
          title: 'pricing.tm-ownership-fee-contains-1-title',
          description: 'pricing.tm-ownership-fee-contains-1-desc',
        },
        {
          title: 'pricing.tm-ownership-fee-contains-2-title',
          description: 'pricing.tm-ownership-fee-contains-2-desc',
        },
        {
          title: 'pricing.tm-ownership-fee-contains-3-title',
          description: 'pricing.tm-ownership-fee-contains-3-desc',
        },
        {
          title: 'pricing.tm-ownership-fee-contains-4-title',
          description: 'pricing.tm-ownership-fee-contains-4-desc',
        },
        {
          title: 'pricing.tm-ownership-fee-contains-5-title',
          description: 'pricing.tm-ownership-fee-contains-5-desc',
        },
      ],
      calculator: {
        description: 'pricing.tm-ownership-calculator-subtitle',
        startButtonText: 'start-ownership-transfer-title',
        continueButtonText: 'pricing.continue-with-ownership-transfer-title',
        assignmentAgreementQuestion: 'pricing.tm-ownership-assignment-agreement-question',
      },
    },
    contactBanner: {
      title: 'pricing-contact-banner-title',
      description: 'pricing-contact-banner-desc',
    },
  }
}

/**
 * Built here rather than inside the tables so the `Offer` a crawler reads and the price a visitor
 * reads cannot come apart.
 */
export type PriceListItem = {
  key: string
  title: string
  titleValues?: Record<string, string | undefined>
  description?: string
  linkUrl?: string
  // what the price is per, where it is not a one-off fee
  priceNote?: string
  price: number
}

export const getLegalServicePriceListItems = (
  group: LegalServiceGroups,
  currency: Currency,
): PriceListItem[] =>
  servicesForPriceList[group].flatMap((key) => {
    const service = legalServices.find((legalService) => legalService.key === key)
    const price = getServicePrices()[supportedServices.Legal]?.[key]?.[currency] ?? 0

    return service && price > 0
      ? [
          {
            key,
            title: service.title,
            description: service.description,
            linkUrl: service.linkUrl,
            price,
          },
        ]
      : []
  })

export const getBrandMonitoringPriceListItems = (currency: Currency): PriceListItem[] =>
  getPricingContent().brandProtection.monitoring.services.map((tier) => ({
    key: tier,
    title: brandMonitoringTiers[tier].pricingTitle ?? brandMonitoringTiers[tier].title,
    priceNote: brandMonitoringTiers[tier].priceNote,
    linkUrl: routeTo.brandMonitoring.home(),
    price: getTierBasePrice(BrandSubscriptionPaymentFrequency.monthly, tier, currency),
  }))

export const getBrandEnforcementPriceListItems = (currency: Currency): PriceListItem[] =>
  getPricingContent().brandProtection.enforcement.services.map(
    ({type, platform, jurisdiction, option}) => {
      const content = getSolutionItemContent(type, platform, jurisdiction, option)
      const {serviceFee, governmentFee} = getSolutionServiceFees(type, currency, {
        platform,
        jurisdiction,
        option,
      })

      return {
        key: [type, jurisdiction, option].filter(Boolean).join('-'),
        title: content.pricingTitle ?? content.title,
        titleValues: {jurisdiction},
        description: content.pricingDescription,
        price: (serviceFee ?? 0) + (governmentFee ?? 0),
      }
    },
  )
